/**
 * 导演台工程读写（P0-8 持久化）。
 *
 * 三条落袋路径，全部复用 model/persist 的「迁移→校验」加载管线，绝不裸 JSON.parse 硬读：
 *  1. 序列化：DirectorProject → JSON 文本（写入前盖当前 schemaVersion）；
 *  2. 自动保存：localStorage 快照（刷新/崩溃不丢当前场景，用户最在意的"摆好就没了"）；
 *  3. 文件存/开：File System Access API（Edge/Chrome 原生存盘对话框），无则回退下载/上传输入框。
 *
 * 分层：序列化与 storage 读写是纯逻辑（storage 注入，可离线测）；文件 API 只在浏览器
 * 事件里调用，且用 typeof window 守卫，测试环境不触达。
 */
import {
  DIRECTOR_SCHEMA_VERSION,
  type DirectorProject,
} from '../../model/director';
import { loadDirectorProject, parseAndLoad, type LoadResult } from '../../model/persist';

/** 序列化：盖当前版本号，缩进 2 便于人读与 diff */
export function serializeDirectorProject(doc: DirectorProject): string {
  return JSON.stringify({ ...doc, schemaVersion: DIRECTOR_SCHEMA_VERSION }, null, 2);
}

/** localStorage 键：带版本后缀，结构大改时旧键自然作废而非污染 */
export const STORAGE_KEY = 'videoai-pro.director.project.v1';

/** 只用到的存储面，便于测试注入假实现 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function globalStorage(): StorageLike | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null; // 隐私模式等禁用 localStorage 的场合，静默降级为不自动保存
  }
}

/** 写入自动保存快照；返回是否成功（无存储或配额异常时 false，调用侧不打断编辑） */
export function saveToStorage(doc: DirectorProject, storage?: StorageLike): boolean {
  const s = storage ?? globalStorage();
  if (s === null) {
    return false;
  }
  try {
    s.setItem(STORAGE_KEY, serializeDirectorProject(doc));
    return true;
  } catch {
    return false;
  }
}

/** 读回自动保存快照；null=无快照；否则为迁移+校验后的结果（可能 ok=false 表损坏） */
export function loadFromStorage(storage?: StorageLike): LoadResult<DirectorProject> | null {
  const s = storage ?? globalStorage();
  if (s === null) {
    return null;
  }
  const raw = s.getItem(STORAGE_KEY);
  if (raw === null) {
    return null;
  }
  return parseAndLoad(raw, loadDirectorProject);
}

export function clearStorage(storage?: StorageLike): void {
  const s = storage ?? globalStorage();
  if (s !== null) {
    try {
      s.removeItem(STORAGE_KEY);
    } catch {
      /* 忽略 */
    }
  }
}

/* ---------- 服务器端真实文件自动保存（P0-8） ---------- */

export interface AutosaveServerMeta {
  savedAt: string;
  path: string;
}

/**
 * 把工程 POST 给本地服务器，由它写入磁盘上的真实文件（绕开浏览器沙箱）。
 * 服务器不可达/未启动时返回 null，调用侧降级为仅 localStorage（不打断编辑）。
 */
export async function autosaveToServer(doc: DirectorProject): Promise<AutosaveServerMeta | null> {
  try {
    const res = await fetch('/api/v2/director/autosave', {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: serializeDirectorProject(doc),
    });
    if (!res.ok) {
      return null;
    }
    const env = (await res.json()) as { ok?: boolean; data?: { savedAt?: string; path?: string } };
    if (env.ok === true && typeof env.data?.savedAt === 'string' && typeof env.data?.path === 'string') {
      return { savedAt: env.data.savedAt, path: env.data.path };
    }
    return null;
  } catch {
    return null; // 服务器未启动等：静默降级
  }
}

/* ---------- 文件存/开（仅浏览器事件里调用，不进单测） ---------- */

export type SaveFileOutcome = 'saved' | 'cancelled' | 'error';
export type OpenFileOutcome = { ok: true; text: string } | { ok: false; reason: 'cancelled' | 'error' };

function isAbort(e: unknown): boolean {
  return e instanceof Error && e.name === 'AbortError';
}

/** 保存工程到文件：优先原生存盘对话框，回退浏览器下载 */
export async function saveProjectFile(doc: DirectorProject): Promise<SaveFileOutcome> {
  const text = serializeDirectorProject(doc);
  const w = window as unknown as {
    showSaveFilePicker?: (opts: unknown) => Promise<{ createWritable: () => Promise<{ write: (d: string) => Promise<void>; close: () => Promise<void> }> }>;
  };
  if (typeof w.showSaveFilePicker === 'function') {
    try {
      const handle = await w.showSaveFilePicker({
        suggestedName: 'scene.json',
        types: [{ description: '导演台工程', accept: { 'application/json': ['.json'] } }],
      });
      const wr = await handle.createWritable();
      await wr.write(text);
      await wr.close();
      return 'saved';
    } catch (e) {
      return isAbort(e) ? 'cancelled' : 'error';
    }
  }
  try {
    const blob = new Blob([text], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'scene.json';
    a.click();
    URL.revokeObjectURL(url);
    return 'saved';
  } catch {
    return 'error';
  }
}

/** 打开工程文件：优先原生选择器，回退隐藏 <input type=file>；返回文本或取消 */
export async function openProjectFile(): Promise<OpenFileOutcome> {
  const w = window as unknown as {
    showOpenFilePicker?: (opts: unknown) => Promise<Array<{ getFile: () => Promise<File> }>>;
  };
  try {
    if (typeof w.showOpenFilePicker === 'function') {
      const handles = await w.showOpenFilePicker({
        types: [{ description: '导演台工程', accept: { 'application/json': ['.json'] } }],
      });
      const handle = handles[0];
      if (handle === undefined) {
        return { ok: false, reason: 'cancelled' };
      }
      const file = await handle.getFile();
      return { ok: true, text: await file.text() };
    }
    const file = await pickViaInput();
    if (file === null) {
      return { ok: false, reason: 'cancelled' };
    }
    return { ok: true, text: await file.text() };
  } catch (e) {
    return { ok: false, reason: isAbort(e) ? 'cancelled' : 'error' };
  }
}

/** 回退方案：隐藏 file input（无原生选择器的浏览器） */
function pickViaInput(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.onchange = () => resolve(input.files && input.files[0] ? input.files[0] : null);
    // 用户取消不触发任何事件：这里无法可靠区分取消，交给外层超时/再点处理
    input.click();
  });
}
